import { Injectable } from '@angular/core';
import { LoadAllMemberships } from '@features/membership/store/actions/membership.actions';
import { UserPreferenceActionTypes } from '@features/user-preference/store/actions/user-preference.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthActionTypes } from '@rd/core/auth/store/actions/auth.actions';
import { HttpError } from '@rd/types';
import { AutoRedemptionSetting } from '@rd/types/auto-redemption-setting.type';
import { of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { AutoRedemptionSettingApiService } from '../../services/auto-redemption-setting-api/auto-redemption-setting-api.service';
import * as actions from '../actions/auto-redemption-settings.actions';

@Injectable()
export class AutoRedemptionSettingsEffects {
  constructor(
    private actions$: Actions,
    private autoRedemptionSettingApiService: AutoRedemptionSettingApiService,
  ) {}

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.LOGIN_USER),
      take(1),
      map(() => new actions.LoadAutoRedemptionSettings()),
    ),
  );

  deleteAutoRedemptionSetting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.AutoRedemptionSettingTypes.DELETE_AUTO_REDEMPTION_SETTING),
      switchMap(
        ({
          payload: { id, loyaltyProgramId },
        }: actions.DeleteAutoRedemptionSetting) =>
          this.autoRedemptionSettingApiService
            .deleteAutoRedemptionSetting(id)
            .pipe(
              map(
                () =>
                  new actions.DeleteAutoRedemptionSettingSuccess({
                    loyaltyProgramId,
                  }),
              ),
              catchError((err) =>
                of(new actions.DeleteAutoRedemptionSettingFail(err)),
              ),
            ),
      ),
    ),
  );

  loadAutoRedemptionSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.AutoRedemptionSettingTypes.LOAD_AUTO_REDEMPTION_SETTINGS),
      switchMap((action: actions.LoadAutoRedemptionSettings) => {
        return this.autoRedemptionSettingApiService
          .getAutoRedemptionSettings()
          .pipe(
            map((autoRedemptionSetting: AutoRedemptionSetting[]) => {
              return new actions.LoadAutoRedemptionSettingsSuccess(
                autoRedemptionSetting,
              );
            }),
            catchError((error: HttpError) =>
              of(new actions.LoadAutoRedemptionSettingsFail(error)),
            ),
          );
      }),
    ),
  );

  updateAutoRedemptionSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.AutoRedemptionSettingTypes.UPDATE_AUTO_REDEMPTION_SETTING),
      switchMap((action: actions.UpdateAutoRedemptionSetting) => {
        return this.autoRedemptionSettingApiService
          .updateAutoRedemptionSetting(
            action.payload.id,
            action.payload.pointsRequired,
            {
              strategy: action.payload.strategy,
              strategyOptions: action.payload.strategyOptions,
            },
          )
          .pipe(
            map((autoRedemptionSetting: AutoRedemptionSetting) => {
              return new actions.UpdateAutoRedemptionSettingSuccess(
                autoRedemptionSetting,
              );
            }),
            catchError((error: HttpError) =>
              of(new actions.UpdateAutoRedemptionSettingFail(error)),
            ),
          );
      }),
    ),
  );

  hydrateAndupdateAutoRedemptionSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.AutoRedemptionSettingTypes
          .HYDRATE_AND_UPDATE_AUTO_REDEMPTION_SETTING,
      ),
      switchMap((action: actions.HydrateAndUpdateAutoRedemptionSetting) => {
        return this.autoRedemptionSettingApiService
          .updateAutoRedemptionSetting(
            action.payload.id,
            action.payload.pointsRequired,
            {
              strategy: action.payload.strategy,
              strategyOptions: action.payload.strategyOptions,
            },
          )
          .pipe(
            map((autoRedemptionSetting: AutoRedemptionSetting) => {
              return new actions.UpdateAutoRedemptionSettingSuccess(
                autoRedemptionSetting,
              );
            }),
            catchError((error: HttpError) =>
              of(new actions.UpdateAutoRedemptionSettingFail(error)),
            ),
          );
      }),
    ),
  );

  createAutoRedemptionSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.AutoRedemptionSettingTypes.CREATE_AUTO_REDEMPTION_SETTING),
      switchMap((action: actions.CreateAutoRedemptionSetting) => {
        return this.autoRedemptionSettingApiService
          .createAutoRedemptionSetting(action.payload)
          .pipe(
            map((autoRedemptionSetting: AutoRedemptionSetting) => {
              return new actions.CreateAutoRedemptionSettingSuccess(
                autoRedemptionSetting,
                action.isEverydaySpend,
              );
            }),
            catchError((error: HttpError) =>
              of(new actions.CreateAutoRedemptionSettingFail(error)),
            ),
          );
      }),
    ),
  );

  // We re-fetch memberships since a user who has created an auto redemption could have possibly created a membership
  // as well
  createAutoRedemptionSettingsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        actions.AutoRedemptionSettingTypes
          .CREATE_AUTO_REDEMPTION_SETTING_SUCCESS,
      ),
      map(() => new LoadAllMemberships()),
    ),
  );

  updatePointsAccountIdSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPreferenceActionTypes.UPDATE_POINTS_ACCOUNT_ID_SUCCESS),
      map(() => new actions.LoadAutoRedemptionSettings()),
    ),
  );
}
